'use client';

import React from 'react';
import { ChevronLeft } from 'lucide-react';

interface Props {
  selected: string[];
  onChange: (styles: string[]) => void;
  onNext: () => void;
  onBack: () => void;
}

const STYLES = [
  { id: 'listen', emoji: '👂', label: 'Listen to me', desc: 'Just be present' },
  { id: 'comfort', emoji: '🤗', label: 'Comfort me', desc: 'Warmth when I need it' },
  { id: 'understand', emoji: '🪞', label: 'Help me understand', desc: 'Make sense of things' },
  { id: 'solve', emoji: '💡', label: 'Help me solve things', desc: 'Practical support' },
  { id: 'accountable', emoji: '🎯', label: 'Keep me accountable', desc: 'Gentle nudges' },
  { id: 'everything', emoji: '💜', label: 'A little of everything', desc: 'Mix it up' },
];

export default function StepSupportStyle({ selected, onChange, onNext, onBack }: Props) {
  const toggle = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  return (
    <div className="flex-1 flex flex-col px-6 py-8 max-w-sm mx-auto w-full slide-up">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-muted-foreground text-sm mb-8 hover:text-foreground transition-colors"
      >
        <ChevronLeft size={18} />
        Back
      </button>

      <div className="flex-1 flex flex-col gap-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground leading-snug">
            How should Clingy support you?
          </h2>
          <p className="text-sm text-muted-foreground mt-2">You can always change this later.</p>
        </div>

        <div className="flex flex-col gap-3">
          {STYLES.map((style) => {
            const isSelected = selected.includes(style.id);
            return (
              <button
                key={`style-${style.id}`}
                onClick={() => toggle(style.id)}
                className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl border text-left transition-all duration-200 active:scale-95 ${
                  isSelected
                    ? 'border-primary bg-primary/10' :'border-border bg-card'
                }`}
              >
                <span className="text-2xl">{style.emoji}</span>
                <div>
                  <p className={`text-sm font-semibold ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                    {style.label}
                  </p>
                  <p className="text-xs text-muted-foreground">{style.desc}</p>
                </div>
                <div className="ml-auto">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                      isSelected ? 'border-primary bg-primary' : 'border-border'
                    }`}
                  >
                    {isSelected && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="pt-6">
        <button
          onClick={onNext}
          disabled={selected.length === 0}
          className="w-full py-4 rounded-2xl lavender-gradient text-white font-semibold text-base shadow-card transition-all duration-200 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Continue
        </button>
        <button
          onClick={onNext}
          className="w-full py-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}